import React, { useState } from 'react';
import { ShoppingCart } from 'lucide-react';
import { Button } from './ui/button';

const ProductBadge = ({ available }) => {
  if (available === true) {
    return (
      <div className="absolute top-4 right-4 z-10 bg-[#D4AF37] text-[#0a0a0a] font-bold px-4 py-2 rounded-full text-sm animate-pulse">
        AVAILABLE NOW
      </div>
    );
  }
  if (available === false) {
    return (
      <div className="absolute top-4 right-4 z-10 bg-[#E63946] text-white font-bold px-4 py-2 rounded-full text-sm">
        COMING SOON
      </div>
    );
  }
  return null;
};

const ProductMedia = ({ product }) => {
  if (product.colors) {
    return (
      <div className="grid grid-cols-3 gap-1 p-2 bg-[#0a0a0a]">
        {product.colors.map((color) => (
          <img
            key={color.name}
            src={color.image}
            alt={`${product.name} - ${color.name}`}
            className="w-full h-48 object-cover rounded-lg"
          />
        ))}
      </div>
    );
  }
  return (
    <img
      src={product.image}
      alt={product.name}
      className="w-full h-80 object-cover"
    />
  );
};

const ProductCTA = () => (
  <Button
    disabled
    className="w-full bg-gray-600 text-gray-400 cursor-not-allowed font-bold py-3"
  >
    COMING SOON
  </Button>
);

const ProductCard = ({ product }) => (
  <div className="bg-gradient-to-br from-[#1a1a1a] to-[#0f0f0f] border border-[#D4AF37]/20 rounded-2xl overflow-hidden transform hover:scale-105 transition-all duration-300 hover:border-[#D4AF37] relative">
    <ProductBadge available={product.available} />
    <ProductMedia product={product} />
    <div className="p-6">
      <h3 className="text-2xl font-bold text-white mb-3" style={{ fontFamily: '"Bebas Neue", sans-serif' }}>
        {product.name}
      </h3>
      <p className="text-gray-400 text-sm mb-4 leading-relaxed">
        {product.description}
      </p>
      {product.colors && (
        <p className="text-xs text-[#D4AF37] mb-4">
          Available in: {product.colors.map(c => c.name).join(', ')}
        </p>
      )}
      <ProductCTA />
    </div>
  </div>
);

const MerchandiseStore = () => {
  const [selectedCategory, setSelectedCategory] = useState('all');

  const tshirts = [
    {
      id: 0,
      name: '40 Songs and a Mic',
      description: 'Bold branded t-shirt featuring the iconic 40 Songs and a Mic logo with gold paint splatter design.',
      image: 'https://customer-assets.emergentagent.com/job_exact-replica-167/artifacts/c61ty7me_40%20songs%20and%20a%20mic%20tshirt.PNG',
      available: false
    },
    {
      id: 1,
      name: 'Shared Humanity',
      description: 'A powerful visual of healing through community and breaking cycles of violence through poetry and connection',
      colors: [
        { name: 'Dark Blue', image: 'https://customer-assets.emergentagent.com/job_exact-replica-167/artifacts/fq6kyahr_Shared%20Humanity%20t-shirt%20%28dark%20blue%29.jpeg' },
        { name: 'Black', image: 'https://customer-assets.emergentagent.com/job_exact-replica-167/artifacts/5nfw7r6c_Shared%20Humanity%20t-shirt%20%28black%29.jpeg' },
        { name: 'Ash Grey', image: 'https://customer-assets.emergentagent.com/job_exact-replica-167/artifacts/puecwf6z_Shared%20Humanity%20t-shirt%20%28ash%20grey%29.jpeg' }
      ],
      available: false
    },
    {
      id: 2,
      name: 'Roots of Selfishness',
      description: 'Every crime has roots. A visual reminder that what is not healed is repeated - exploring the trauma roots beneath harmful behavior',
      image: 'https://customer-assets.emergentagent.com/job_exact-replica-167/artifacts/2csdib3u_Roots%20of%20Selfishness%20%28black%29.jpeg',
      available: false
    },
    {
      id: 3,
      name: 'Supreme Refinement',
      description: 'Healing is a process - keep going. Features the three-tier gasoline transformation system from crude oil to refined purpose',
      image: 'https://customer-assets.emergentagent.com/job_exact-replica-167/artifacts/49kfn5x1_Supreme%20Refinement%20%28black%29.jpeg',
      available: false
    },
    {
      id: 4,
      name: 'The Healing Process',
      description: 'Visual metaphor of extracting childhood trauma like crude oil from deep beneath - bringing buried pain to the surface for healing',
      image: 'https://customer-assets.emergentagent.com/job_exact-replica-167/artifacts/xps2zbww_The%20Healing%20Process%20t-shirt%20%28black%29.jpeg',
      available: false
    },
    {
      id: 5,
      name: 'Broken Lenses',
      description: 'Healing is not erasing the cracks - it\'s learning to see through them differently. Two perspectives: trauma vs. transformation',
      image: 'https://customer-assets.emergentagent.com/job_exact-replica-167/artifacts/yx98s6kv_Broken%20Lenses%20%28black%29%20front.jpeg',
      available: false
    }
  ];

  const posters = [
    {
      id: 6,
      name: 'Shared Humanity Poster',
      description: 'Full-size educational poster showing healing through poetry and breaking the cycle of violence through community',
      image: 'https://customer-assets.emergentagent.com/job_exact-replica-167/artifacts/mucf79x4_Shared%20Humanity%20%28Poster%29.jpeg',
      available: false
    },
    {
      id: 7,
      name: 'Roots of Selfishness Poster',
      description: 'Detailed visual exploration of trauma roots: abandonment, neglect, abuse, fear, and rejection - understanding the cycle',
      image: 'https://customer-assets.emergentagent.com/job_exact-replica-167/artifacts/w38hh84s_Roots%20of%20Selfishness%20%28Poster%29.jpeg',
      available: false
    },
    {
      id: 8,
      name: 'Supreme Refinement Poster',
      description: 'Complete visual map of the Gasoline philosophy - from crude oil (trauma) through the refining process to growth and freedom',
      image: 'https://customer-assets.emergentagent.com/job_exact-replica-167/artifacts/0dwwzeeq_Superme%20Refined%20%28Poster%29.jpeg',
      available: false
    },
    {
      id: 9,
      name: 'The Healing Process Poster',
      description: 'Powerful imagery of extracting buried childhood trauma - the oil rig metaphor for healing work and transformation',
      image: 'https://customer-assets.emergentagent.com/job_exact-replica-167/artifacts/wcnm652k_The%20Healing%20Process%20%28Poster%29.jpeg',
      available: false
    },
    {
      id: 10,
      name: 'Broken Lenses Poster',
      description: 'Split perspective showing dark trauma lens vs. light healing lens - learning to see through your cracks differently',
      image: 'https://customer-assets.emergentagent.com/job_exact-replica-167/artifacts/4sxnwdkl_Broken%20Lenses%20%28Poster%29.jpeg',
      available: false
    }
  ];

  const allProducts = selectedCategory === 'all' 
    ? [...tshirts, ...posters]
    : selectedCategory === 'tshirts' 
    ? tshirts 
    : posters;

  return (
    <section id="merch-store" className="relative py-24 bg-gradient-to-b from-[#0a0a0a] to-[#1a1a1a]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center space-x-2 bg-[#D4AF37]/10 border border-[#D4AF37]/30 rounded-full px-6 py-2 mb-6">
            <ShoppingCart className="w-5 h-5 text-[#D4AF37]" />
            <span className="text-[#D4AF37] font-bold text-sm">MERCHANDISE STORE</span>
          </div>
          <h2 className="text-5xl sm:text-6xl md:text-7xl font-bold text-white mb-6" style={{ fontFamily: '"Bebas Neue", sans-serif' }}>
            WEAR YOUR
            <span className="block text-transparent bg-clip-text bg-gradient-to-r from-[#D4AF37] to-[#FFD700]">
              HEALING JOURNEY
            </span>
          </h2>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            Every purchase supports trauma-informed programs and healing initiatives
          </p>
        </div>

        {/* Category Filter */}
        <div className="flex justify-center gap-4 mb-12">
          <Button
            onClick={() => setSelectedCategory('all')}
            className={`${
              selectedCategory === 'all'
                ? 'bg-gradient-to-r from-[#D4AF37] to-[#FFD700] text-[#0a0a0a]'
                : 'bg-[#1a1a1a] text-gray-300 border border-[#D4AF37]/30'
            } font-bold px-8 py-6 transition-all duration-300`}
          >
            ALL ITEMS
          </Button>
          <Button
            onClick={() => setSelectedCategory('tshirts')}
            className={`${
              selectedCategory === 'tshirts'
                ? 'bg-gradient-to-r from-[#D4AF37] to-[#FFD700] text-[#0a0a0a]'
                : 'bg-[#1a1a1a] text-gray-300 border border-[#D4AF37]/30'
            } font-bold px-8 py-6 transition-all duration-300`}
          >
            T-SHIRTS
          </Button>
          <Button
            onClick={() => setSelectedCategory('posters')}
            className={`${
              selectedCategory === 'posters'
                ? 'bg-gradient-to-r from-[#D4AF37] to-[#FFD700] text-[#0a0a0a]'
                : 'bg-[#1a1a1a] text-gray-300 border border-[#D4AF37]/30'
            } font-bold px-8 py-6 transition-all duration-300`}
          >
            POSTERS
          </Button>
        </div>

        {/* Products Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {allProducts.map((product) => (
            <ProductCard key={product.id} product={product} />
          ))}
        </div>

        {/* Bottom Message */}
        <div className="mt-16 text-center bg-gradient-to-r from-[#1a1a1a] via-[#D4AF37]/5 to-[#1a1a1a] border border-[#D4AF37]/30 rounded-2xl p-8">
          <p className="text-2xl text-white font-semibold mb-2">
            Merchandise coming soon
          </p>
          <p className="text-gray-400 mb-6">
            We are building a new way for you to purchase our merch and support the healing work. Check back shortly.
          </p>
          <Button
            onClick={() => document.getElementById('workbooks')?.scrollIntoView({ behavior: 'smooth' })}
            data-testid="merch-cta-workbooks"
            className="w-full sm:w-auto max-w-full bg-gradient-to-r from-[#D4AF37] to-[#FFD700] text-[#0a0a0a] hover:from-[#FFD700] hover:to-[#D4AF37] font-bold px-6 sm:px-10 py-6 text-base sm:text-xl whitespace-normal leading-tight break-words transition-all duration-300 transform hover:scale-105"
          >
            <ShoppingCart className="w-5 h-5 mr-2 flex-shrink-0" />
            SHOP WORKBOOKS INSTEAD
          </Button>
        </div>
      </div>
    </section>
  );
};

export default MerchandiseStore;
